"use client";

import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Script from "next/script";
import { useRouter } from "next/navigation";

import { Button } from "@/components/ui/button";
import { createLumiApiClient } from "@/lib/lumi-api";

type Step = "email" | "code";

interface LumiAuthFormProps {
  mode: "sign-in" | "sign-up";
}

/* Mobile Safari zooms the whole page when a focused input's font-size is under
   16px. These inputs sit inside a `text-sm` label, so they inherited 14px and
   zoomed on every tap. `text-base` pins them at 16px; `min-h-11` gives the 44px
   target WCAG 2.5.5 asks for (they were ~37px). */
const INPUT_CLASS =
  "mt-1 block min-h-11 w-full rounded-md border border-rose-quartz bg-white px-3 py-2 text-base text-space-cadet outline-none focus:border-teal-accent focus:ring-2 focus:ring-teal-accent";

export default function LumiAuthForm({ mode }: LumiAuthFormProps) {
  const router = useRouter();
  const api = useMemo(() => createLumiApiClient(), []);
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleInitializedRef = useRef(false);
  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID;

  const errorId = "lumi-auth-error";

  const finishAuthentication = useCallback(() => {
    router.push("/library");
    router.refresh();
  }, [router]);

  const submitGoogleCredential = useCallback(
    async (response: GoogleCredentialResponse) => {
      if (!response.credential) {
        setError("Google did not return a sign-in credential");
        return;
      }

      setLoading(true);
      setError(null);
      try {
        await api.authenticateWithGoogle({ credential: response.credential });
        finishAuthentication();
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to sign in with Google",
        );
      } finally {
        setLoading(false);
      }
    },
    [api, finishAuthentication],
  );

  const initializeGoogleIdentity = useCallback(() => {
    if (
      !googleClientId ||
      !window.google?.accounts.id ||
      !googleButtonRef.current ||
      googleInitializedRef.current
    ) {
      return;
    }

    const googleId = window.google.accounts.id;
    googleId.initialize({
      client_id: googleClientId,
      callback: submitGoogleCredential,
      auto_select: false,
      cancel_on_tap_outside: true,
    });
    googleButtonRef.current.replaceChildren();
    googleId.renderButton(googleButtonRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: mode === "sign-up" ? "signup_with" : "signin_with",
      shape: "rectangular",
      logo_alignment: "left",
      width: 350,
    });
    googleInitializedRef.current = true;

    if (mode === "sign-in" && step === "email") {
      googleId.prompt();
    }
  }, [googleClientId, mode, step, submitGoogleCredential]);

  useEffect(() => {
    initializeGoogleIdentity();
    return () => {
      window.google?.accounts.id.cancel();
      googleInitializedRef.current = false;
    };
  }, [initializeGoogleIdentity]);

  async function submitEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.startAuth(
        email,
        mode === "sign-up" ? displayName : undefined,
      );
      setDevCode(response.dev_code ?? null);
      setStep("code");
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to start login");
    } finally {
      setLoading(false);
    }
  }

  async function submitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.verifyAuth(email, code);
      finishAuthentication();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to verify code");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen w-full flex-col justify-center bg-isabelline px-6 py-10">
      <Link className="mx-auto mb-6 block w-full max-w-md text-center" href="/">
        {/* The default wordmark is forced white by a feColorMatrix inside the
            SVG, so CSS cannot recolor it — against this isabelline background
            it measured 1.20:1 and was effectively invisible. `-ink` is the same
            artwork with that matrix set to space-cadet. */}
        <Image
          className="mx-auto h-9 w-auto"
          src="/lumi-logo-ink.svg"
          alt="Lumi"
          width={120}
          height={36}
          priority
        />
      </Link>
      {/* pale-dogwood borders measured 2.10:1 on white and 1.75:1 on the page
          background, under the 3:1 that 1.4.11 requires of control bounds.
          rose-quartz is the next step down the same brand ramp and clears it. */}
      <div className="mx-auto w-full max-w-md rounded-lg border border-rose-quartz bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-space-cadet">
          {mode === "sign-up" ? "Create your Lumi account" : "Sign in to Lumi"}
        </h1>
        <p className="mt-1 text-sm text-ultra-violet">
          {mode === "sign-up"
            ? "Use Google or a one-time email code — no password needed."
            : "Use Google or enter your email for a one-time login code."}
        </p>

        {googleClientId && step === "email" && (
          <>
            <Script
              id="google-identity-services"
              src="https://accounts.google.com/gsi/client"
              strategy="afterInteractive"
              onLoad={initializeGoogleIdentity}
              onReady={initializeGoogleIdentity}
              onError={() =>
                setError("Google sign-in could not be loaded. Use email instead.")
              }
            />
            <div
              className="mt-6 flex min-h-11 w-full justify-center overflow-hidden"
              aria-label={
                mode === "sign-up" ? "Sign up with Google" : "Sign in with Google"
              }
              ref={googleButtonRef}
            />
            <div className="my-5 flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-rose-quartz" />
              <span className="text-xs font-medium uppercase tracking-wide text-ultra-violet">
                or use email
              </span>
              <span className="h-px flex-1 bg-rose-quartz" />
            </div>
          </>
        )}

        {step === "email" ? (
          <form className="mt-6 space-y-4" onSubmit={submitEmail}>
            {mode === "sign-up" && (
              <label className="block text-sm font-medium text-ultra-violet">
                Name
                <input
                  className={INPUT_CLASS}
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  autoComplete="name"
                />
              </label>
            )}
            <label className="block text-sm font-medium text-ultra-violet">
              Email
              <input
                className={INPUT_CLASS}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                type="email"
                /* Phone keyboards capitalize and autocorrect by default, which
                   silently mangles an address as it is typed. */
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
              />
            </label>
            <Button
              className="min-h-11 w-full"
              disabled={loading}
              type="submit"
              aria-busy={loading}
            >
              {loading ? "Sending..." : "Send login code"}
            </Button>
          </form>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={submitCode}>
            <label className="block text-sm font-medium text-ultra-violet">
              Login code
              <input
                className={`${INPUT_CLASS} tracking-widest`}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode="numeric"
                /* Lets iOS and Android offer the code straight from the
                   notification instead of making you switch apps to read it. */
                autoComplete="one-time-code"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                required
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
              />
            </label>
            <p className="text-sm text-ultra-violet">We sent a code to {email}.</p>
            {devCode && (
              <p className="rounded-md border border-teal-accent/30 bg-teal-accent/5 px-3 py-2 text-sm text-teal-accent">
                Development code: <strong>{devCode}</strong>
              </p>
            )}
            <Button
              className="min-h-11 w-full"
              disabled={loading}
              type="submit"
              aria-busy={loading}
            >
              {loading ? "Verifying..." : "Continue"}
            </Button>
          </form>
        )}

        {/* role="alert" so a screen reader announces the failure. Without it
            the message rendered silently and a non-sighted user just saw the
            form do nothing. */}
        {error && (
          <p
            className="mt-4 text-sm text-red-700"
            id={errorId}
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
      <p className="mt-4 text-center text-sm text-ultra-violet">
        {mode === "sign-in" ? "New to Lumi? " : "Already have an account? "}
        <Link
          className="font-medium text-teal-accent underline underline-offset-2"
          href={mode === "sign-in" ? "/sign-up" : "/sign-in"}
        >
          {mode === "sign-in" ? "Create an account" : "Sign in"}
        </Link>
      </p>
    </main>
  );
}
